/**
 * Product Info Tabs functionality
 * Handles tab switching and FAQ accordion behavior
 */

class ProductInfoTabs {
  constructor() {
    this.init();
  }

  init() {
    this.setupTabSwitching();
    this.setupFAQAccordion();
  }

  /**
   * Setup tab switching functionality
   */
  setupTabSwitching() {
    const tabHeaders = document.querySelectorAll('.product-info-tab__header');
    const tabPanels = document.querySelectorAll('.product-info-tab__panel');

    if (!tabHeaders.length || !tabPanels.length) return;

    tabHeaders.forEach(header => {
      header.addEventListener('click', (e) => {
        const targetTab = header.getAttribute('data-tab');
        const targetPanel = document.querySelector(`.product-info-tab__panel[data-panel="${targetTab}"]`);
        
        if (!targetPanel) return;

        // Check if mobile (accordion mode)
        const isMobile = window.innerWidth <= 989;
        
        if (isMobile) {
          // Accordion behavior: toggle individual panel
          const isActive = header.classList.contains('active');
          
          if (isActive) {
            header.classList.remove('active');
            targetPanel.classList.remove('active');
            header.setAttribute('aria-expanded', 'false');
          } else {
            header.classList.add('active');
            targetPanel.classList.add('active');
            header.setAttribute('aria-expanded', 'true');
          }
        } else {
          // Tab behavior: switch between tabs
          // Remove active class from all headers and panels
          tabHeaders.forEach(h => {
            h.classList.remove('active');
            h.setAttribute('aria-expanded', 'false');
          });
          tabPanels.forEach(p => p.classList.remove('active'));

          // Add active class to clicked header
          header.classList.add('active');
          header.setAttribute('aria-expanded', 'true');
          targetPanel.classList.add('active');

          // Announce to screen readers
          this.announceTabChange(header.textContent);
        }
      });

      // Keyboard accessibility
      header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          header.click();
        }
      });
      
      // Set initial aria-expanded based on active state
      header.setAttribute('aria-expanded', header.classList.contains('active') ? 'true' : 'false');
    });
    
    // On mobile, close all panels by default (accordion starts collapsed)
    if (window.innerWidth <= 989) {
      tabHeaders.forEach(h => {
        h.classList.remove('active');
        h.setAttribute('aria-expanded', 'false');
      });
      tabPanels.forEach(p => p.classList.remove('active'));
    }

    // Handle window resize to reset state when switching between mobile/desktop
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const isMobile = window.innerWidth <= 989;
        if (!isMobile) {
          // Reset to desktop tab mode - ensure one tab is always active
          const hasActiveTab = Array.from(tabHeaders).some(h => h.classList.contains('active'));
          if (!hasActiveTab && tabHeaders.length > 0) {
            tabHeaders[0].click();
          }
        }
      }, 250);
    });
  }

  /**
   * Setup FAQ accordion functionality
   */
  setupFAQAccordion() {
    const faqToggles = document.querySelectorAll('[data-faq-toggle]');

    if (!faqToggles.length) return;

    faqToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleFAQ(toggle);
      });

      // Keyboard accessibility
      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleFAQ(toggle);
        }
      });
    });
  }

  /**
   * Toggle individual FAQ item
   * @param {HTMLElement} toggle - The FAQ toggle button
   */
  toggleFAQ(toggle) {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const content = toggle.nextElementSibling;

    if (!content || !content.hasAttribute('data-faq-content')) return;

    // Toggle aria-expanded attribute
    toggle.setAttribute('aria-expanded', !isExpanded);

    // Toggle content visibility
    if (isExpanded) {
      content.setAttribute('hidden', '');
    } else {
      content.removeAttribute('hidden');
    }

    // Smooth scroll into view if needed
    if (!isExpanded) {
      setTimeout(() => {
        const rect = toggle.getBoundingClientRect();
        const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
        
        if (!isInViewport) {
          toggle.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 300);
    }
  }

  /**
   * Announce tab change to screen readers
   * @param {string} tabName - Name of the activated tab
   */
  announceTabChange(tabName) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.classList.add('visually-hidden');
    announcement.textContent = `${tabName} tab selected`;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductInfoTabs();
  });
} else {
  new ProductInfoTabs();
}

// Re-initialize if section is dynamically loaded (e.g., theme editor)
document.addEventListener('shopify:section:load', () => {
  new ProductInfoTabs();
});
